<div 
  [showIfHasCapability]="[
    {
      capabilityString: 'CanAccessSupportWing',
      type: 'system'
    }
  ]"
  class="content85percent capabilities-margin">
  
  <div class="page-header-bar mt-3">
    <h1>Support: Today's Orders Report</h1>
    <div class="float-end">
      <div class="d-flex align-items-center">
        <mat-slide-toggle
          class="me-3"
          [(ngModel)]="autoRefreshEnabled"
          (change)="toggleAutoRefresh()"
          [matTooltip]="'Auto-refresh data every 1 minute for live monitoring'"
          labelPosition="before">
          <span class="mat-slide-toggle-content">
            Auto-Refresh
          </span>
        </mat-slide-toggle>

        <button 
          type="button"
          class="btn btn-primary" 
          [disabled]="isCopyingEmails"
          (click)="onCopySigningAgentEmails()"
          title="Copies signing agent emails from orders with these statuses: Ready, In Progress, Signing, Session Complete, Vaulting">
          <span *ngIf="isCopyingEmails" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
          <fa-icon [icon]="faCopy" *ngIf="!isCopyingEmails"></fa-icon>
          {{ isCopyingEmails ? 'Copying...' : 'Copy Signing Agent Emails' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Note about data filtering -->
  <p class="text-muted mb-3">
    <strong>Note:</strong> All values are based on orders scheduled for today, except the failed orders card which is based on orders updated today
  </p>

  <!-- Order Summary Cards -->
  <div class="row mb-4" *ngIf="orderSummary$ | async as summary">
    <div class="col-md-3">
      <div class="card report-card complete-card">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalScheduledToday }}</h4>
              <p class="card-text">Scheduled Orders: Ready</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faCalendar" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-md-3">
      <div class="card report-card info-card">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalInProgress }}</h4>
              <p class="card-text">Active Orders: In Progress, Signing, Session Complete, Vaulting</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faPen" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-md-3">
      <div class="card report-card ready-card">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalCompletedToday }}</h4>
              <p class="card-text">Complete</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faCheckCircle" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-md-3">
      <div class="card report-card error-card">
        <div class="card-body">
          <div class="d-flex justify-content-between">
            <div>
              <h4 class="card-title">{{ summary.totalFailedToday }}</h4>
              <p class="card-text">Signing Failed, Opt Out, Vaulting Failed, Cancelled</p>
            </div>
            <div class="align-self-center">
              <fa-icon [icon]="faExclamationTriangle" size="2x"></fa-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Active Orders Details -->
  <div class="card" *ngIf="orderSummary$ | async">
    <div class="card-header">
      <h3 class="mb-0">Active Orders</h3>
    </div>
    <div class="card-body">
      <div *ngIf="inProgressOrders$ | async as orders; else loadingOrders">
        <div *ngIf="orders.length === 0" class="text-center py-4">
          <p class="text-muted">No active orders found.</p>
        </div>

        <div *ngFor="let order of orders" class="mb-3 border rounded">
          <!-- Collapsed Summary View -->
          <button 
            type="button"
            class="order-summary-toggle w-100 text-start p-3"
            (click)="toggleOrderExpansion(order.orderId)"
            [attr.aria-expanded]="isOrderExpanded(order.orderId)"
            [attr.aria-controls]="'order-details-' + order.orderId">
            <div class="row align-items-center">
              <div class="col-md-8">
                <div class="d-flex align-items-center mb-2">
                  <fa-icon 
                    [icon]="isOrderExpanded(order.orderId) ? faChevronUp : faChevronDown" 
                    class="me-2 text-muted">
                  </fa-icon>
                  <h6 class="mb-0">
                    Order: #{{ order.orderId }} | Loan: {{ order.loanIdentifier }} | 3rd Party: {{ order.thirdPartyOrderId }}
                  </h6>
                </div>
                <div class="row">
                  <div class="col-sm-2">
                    <small><strong>Status:</strong><br>
                      {{ order.statusCode }}
                    </small>
                  </div>
                  <div class="col-sm-1">
                    <small><strong>Type:</strong><br>
                      <span class="text-truncate d-inline-block" style="max-width: 100%;">{{ order.orderType }}</span>
                    </small>
                  </div>
                  <div class="col-sm-3">
                    <small><strong>Client:</strong><br>
                      <span class="text-truncate d-inline-block" style="max-width: 100%;">{{ order.clientName }}</span>
                    </small>
                  </div>
                  <div class="col-sm-2">
                    <small><strong>Scheduled:</strong><br>
                      {{ formatDateTime(order.scheduledDateTime) }}
                    </small>
                  </div>
                  <div class="col-sm-4">
                    <small><strong>Signing Agent:</strong><br>
                      <span *ngIf="order.signingAgentEmails.length > 0">
                        {{ order.signingAgentEmails[0] }}
                      </span>
                      <span *ngIf="order.signingAgentEmails.length === 0" class="text-muted">
                        None assigned
                      </span>
                    </small>
                  </div>
                </div>
              </div>
              <div class="col-md-4 text-end">
                <div *ngIf="order.statusUpdatedDate">
                  <small class="text-muted">
                    Updated: {{ formatUpdatedDateTime(order.statusUpdatedDate) }}
                  </small>
                </div>
              </div>
            </div>
          </button>

          <!-- Expanded Detailed View -->
          <div 
            *ngIf="isOrderExpanded(order.orderId)" 
            class="border-top p-3"
            [id]="'order-details-' + order.orderId">
            
            <!-- Order Progress Tracker -->
            <div class="mb-4">
              <h6 class="mb-3">Order Progress</h6>
              <div class="progress-tracker">
                <div class="progress-step ready-step" [class.completed]="getStepCompleted('READY', order.statusCode)" [class.current]="order.statusCode === 'READY'">
                  <div class="step-circle">
                    <fa-icon [icon]="faCalendar" *ngIf="getStepCompleted('READY', order.statusCode) || order.statusCode === 'READY'"></fa-icon>
                  </div>
                  <div class="step-label">Ready</div>
                </div>
                <div class="progress-line" [class.completed]="['INPROGRESS','SIGNING','SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)" [class.active-line]="['INPROGRESS','SIGNING','SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)"></div>
                
                <div class="progress-step active-step" [class.completed]="getStepCompleted('INPROGRESS', order.statusCode)" [class.current]="order.statusCode === 'INPROGRESS'">
                  <div class="step-circle">
                    <fa-icon [icon]="faPen" *ngIf="getStepCompleted('INPROGRESS', order.statusCode) || order.statusCode === 'INPROGRESS'"></fa-icon>
                  </div>
                  <div class="step-label">In Progress</div>
                </div>
                <div class="progress-line" [class.completed]="['SIGNING','SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)" [class.active-line]="['SIGNING','SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)"></div>
                
                <div class="progress-step active-step" [class.completed]="getStepCompleted('SIGNING', order.statusCode)" [class.current]="order.statusCode === 'SIGNING'">
                  <div class="step-circle">
                    <fa-icon [icon]="faPen" *ngIf="getStepCompleted('SIGNING', order.statusCode) || order.statusCode === 'SIGNING'"></fa-icon>
                  </div>
                  <div class="step-label">Signing</div>
                </div>
                <div class="progress-line" [class.completed]="['SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)" [class.active-line]="['SESSIONCOMPLETE','VAULTING','COMPLETE'].includes(order.statusCode)"></div>
                
                <div class="progress-step active-step" [class.completed]="getStepCompleted('SESSIONCOMPLETE', order.statusCode)" [class.current]="order.statusCode === 'SESSIONCOMPLETE'">
                  <div class="step-circle">
                    <fa-icon [icon]="faPen" *ngIf="getStepCompleted('SESSIONCOMPLETE', order.statusCode) || order.statusCode === 'SESSIONCOMPLETE'"></fa-icon>
                  </div>
                  <div class="step-label">Session Complete</div>
                </div>
                <div class="progress-line" [class.completed]="['VAULTING','COMPLETE'].includes(order.statusCode)" [class.active-line]="['VAULTING','COMPLETE'].includes(order.statusCode)"></div>
                
                <div class="progress-step active-step" [class.completed]="getStepCompleted('VAULTING', order.statusCode)" [class.current]="order.statusCode === 'VAULTING'">
                  <div class="step-circle">
                    <fa-icon [icon]="faPen" *ngIf="getStepCompleted('VAULTING', order.statusCode) || order.statusCode === 'VAULTING'"></fa-icon>
                  </div>
                  <div class="step-label">Vaulting</div>
                </div>
                <div class="progress-line" [class.completed]="order.statusCode === 'COMPLETE'" [class.complete-line]="order.statusCode === 'COMPLETE'"></div>
                
                <div class="progress-step complete-step" [class.completed]="getStepCompleted('COMPLETE', order.statusCode)" [class.current]="order.statusCode === 'COMPLETE'">
                  <div class="step-circle">
                    <fa-icon [icon]="faCheckCircle" *ngIf="getStepCompleted('COMPLETE', order.statusCode) || order.statusCode === 'COMPLETE'"></fa-icon>
                  </div>
                  <div class="step-label">Complete</div>
                </div>
              </div>
            </div>

            <div class="mb-3">
              <div class="row">
                <div class="col-md-12">
                  <p class="mb-2">
                    <strong>Order Link:</strong> 
                    <a [routerLink]="['/order-detail', order.orderId]" class="text-decoration-none">
                      View Order Details
                    </a>
                  </p>
                </div>
              </div>
            </div>

            <!-- Participants Detail Table -->
            <div class="mt-3">
              <h6>Participants:</h6>
              <div class="table-responsive">
                <table class="table table-sm table-striped">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let participant of order.participants">
                      <td>{{ participant.firstName }} {{ participant.lastName }}</td>
                      <td>{{ participant.role }}</td>
                      <td>
                        <span *ngIf="participant.email">{{ participant.email }}</span>
                        <span *ngIf="!participant.email" class="text-muted">No email</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <ng-template #loadingOrders>
    <div class="text-center py-4">
      <div class="spinner-border text-dark" role="status">
        <span class="visually-hidden">Loading active orders...</span>
      </div>
      <p class="mt-2 text-muted">Loading active orders...</p>
    </div>
  </ng-template>
</div>
